import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.3 Tier 1 — Cash Flow page. Handles:
//   - view tab switching between Forecast / By Line / Overrides
//     (By-Line and Forecast pre-render server-side; Overrides is
//     a static CRUD grid that's already rendered on first load)
//   - Configure dropdown (grain / horizon / schedule) → PATCH settings
//     and reload the page since the bucketed math shifts on every knob
//   - As-of date picker → full form submit (same as EVA)
//   - Override CRUD via DOM patches on the Overrides tab (no reload)
//
// DOM-patch pattern mirrors `eva_controller.js` (link rows there;
// override rows here). Full reloads only happen on settings save —
// grain and horizon reshape every bucket, so reload is simpler than
// patching three server-side views in JS.
export default class extends Controller {
  static targets = [
    "viewTabButton", "viewPanel",
    "asOfForm", "asOfInput",
    "grain", "horizon", "schedule",
    "overrideModal", "overrideModalTitle",
    "overrideLine", "overrideDueDate", "overrideAmount", "overrideReason",
    "overrideError", "overrideDeleteButton",
    "overrideTableBody", "overrideRow", "overrideEmptyRow",
    "deleteConfirmModal",
    "captureModal", "captureLabel", "captureKind",
    "forecastsModal",
    "forecastSelect",
    "byLineGrid", "byLineRow", "gridCell", "rowTotal", "columnTotal", "grandTotal",
    "importModal", "importDropzone", "importDropArea", "importFileInput",
    "importLoading", "importReview", "importRows", "importSummary",
    "importError", "importApplyButton"
  ]

  static values = {
    projectId: Number,
    budgetId: Number,
    settingsUrl: String,
    activeView: String,
    grain: String,
    horizon: Number,
    bulkUrl: String,
    importUrl: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editingOverrideId = null
    this._pendingDeleteId = null
    this._contingencyViewed = false
    this._showPendingFlash()
    if (sessionStorage.getItem("cash_flow_open_forecasts_modal") === "1") {
      sessionStorage.removeItem("cash_flow_open_forecasts_modal")
      this.showForecastsModal()
    }
    if (this.activeViewValue === "forecast") this._maybeTrackContingencyRow()
  }

  _maybeTrackContingencyRow() {
    if (this._contingencyViewed) return
    const el = this.element.querySelector("[data-cash-flow-contingency]")
    if (!el) return
    this._contingencyViewed = true
    this._track("Cash Flow Contingency Row Viewed", {
      contingency_bac: parseFloat(el.dataset.contingencyBac || "0"),
      drawn_buckets: parseInt(el.dataset.contingencyDrawnBuckets || "0", 10)
    })
  }

  // ── View tabs ──────────────────────────────────────────────────────
  switchView(event) {
    const target = event.currentTarget
    const nextView = target.dataset.viewParam
    if (!nextView || nextView === this.activeViewValue) return

    const fromView = this.activeViewValue
    this._track("Cash Flow View Switched", { from_view: fromView, to_view: nextView })

    const panelAlreadyRendered = this._panelFor(nextView)?.children.length > 0
    if (panelAlreadyRendered) {
      this._setActiveView(nextView)
      if (nextView === "forecast") this._maybeTrackContingencyRow()
      return
    }

    this._fetchAndRenderPanel(nextView).then(() => {
      if (nextView === "forecast") this._maybeTrackContingencyRow()
    })
  }

  // ── Editable By Line grid (item 2) ─────────────────────────────────
  // Cells write `CashFlowOverride` rows keyed on the bucket START date.
  // Blank or zero clears the override so the derived forecast returns.
  // Every commit goes through the bulk endpoint — one cell or a whole
  // pasted block, same round trip.

  onCellFocus(event) {
    event.currentTarget.select()
  }

  onCellKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      const cell = event.currentTarget
      cell.value = cell.dataset.original || ""
      cell.blur()
    }
  }

  async onCellCommit(event) {
    const cell = event.currentTarget
    const next = cell.value.trim()
    if (next === (cell.dataset.original || "").trim()) return

    const ok = await this._saveCells([cell])
    if (ok) this._track("Cash Flow Cell Edited", { cleared: next === "" })
  }

  // Excel paste: \n rows, \t columns, filling right and down from the
  // focused cell. Anything past the edge of the grid is ignored.
  async onCellPaste(event) {
    const text = event.clipboardData?.getData("text/plain")
    if (!text || !/[\t\n]/.test(text)) return // single value → let the browser handle it

    event.preventDefault()
    const start = event.currentTarget
    const rows = text.replace(/\r/g, "").replace(/\n+$/, "").split("\n")
    const startRowIdx = this.byLineRowTargets.indexOf(start.closest("tr"))
    const startColIdx = parseInt(start.dataset.bucketIndex, 10)
    if (startRowIdx < 0 || !Number.isFinite(startColIdx)) return

    const touched = []
    rows.forEach((rowText, r) => {
      const rowEl = this.byLineRowTargets[startRowIdx + r]
      if (!rowEl) return
      rowText.split("\t").forEach((value, c) => {
        const cell = rowEl.querySelector(`[data-cash-flow-target="gridCell"][data-bucket-index="${startColIdx + c}"]`)
        if (!cell) return
        cell.value = value.trim()
        touched.push(cell)
      })
    })
    if (touched.length === 0) return

    const ok = await this._saveCells(touched)
    if (ok) this._track("Cash Flow Grid Pasted", { cell_count: touched.length })
  }

  async _saveCells(cells) {
    const payload = {
      cells: cells.map(cell => ({
        budget_line_item_id: parseInt(cell.dataset.lineId, 10),
        due_date: cell.dataset.dueDate,
        amount: cell.value.trim()
      }))
    }

    cells.forEach(cell => cell.classList.add("opacity-50"))
    const resp = await this._ajaxJSON("POST", this.bulkUrlValue, payload)
    cells.forEach(cell => cell.classList.remove("opacity-50"))

    if (!resp.ok) {
      cells.forEach(cell => { cell.value = cell.dataset.original || "" })
      this._showToast(resp.body?.error || resp.body?.errors?.join(", ") || "Couldn't save those cells", "error")
      return false
    }
    if (resp.body?.errors?.length) {
      this._showToast(resp.body.errors.join(", "), "error")
    }

    cells.forEach(cell => this._markCellSaved(cell))
    this._recomputeGridTotals()
    return true
  }

  // Repaints one cell to its new state: entered (brand) vs derived
  // (muted italic). A cleared cell keeps showing whatever the server
  // now derives, which we can't know without a reload — so it is left
  // blank and flagged derived until the next page load fills it in.
  _markCellSaved(cell) {
    const value = cell.value.trim()
    cell.dataset.original = value
    cell.title = value === "" ? "" : "Updated by you · just now · typed edit"
    const isEntered = value !== "" && this._parseNumber(value) !== 0
    cell.dataset.derived = isEntered ? "false" : "true"
    cell.classList.remove("text-brand", "font-medium", "text-gray-500", "italic", "text-gray-300")
    cell.parentElement?.classList.toggle("bg-brand/5", isEntered)
    if (isEntered) {
      cell.classList.add("text-brand", "font-medium")
    } else if (value === "") {
      cell.classList.add("text-gray-300")
    } else {
      cell.classList.add("text-gray-500", "italic")
    }
  }

  _recomputeGridTotals() {
    const columnSums = {}
    let grand = 0

    this.byLineRowTargets.forEach(row => {
      let rowSum = 0
      row.querySelectorAll('[data-cash-flow-target="gridCell"]').forEach(cell => {
        const value = this._parseNumber(cell.value)
        const col = cell.dataset.bucketIndex
        columnSums[col] = (columnSums[col] || 0) + value
        rowSum += value
      })
      const totalCell = this.rowTotalTargets.find(t => t.dataset.lineId === row.dataset.lineId)
      if (totalCell) totalCell.textContent = this._formatMoney(rowSum)
      grand += rowSum
    })

    this.columnTotalTargets.forEach(cell => {
      cell.textContent = this._formatMoney(columnSums[cell.dataset.bucketIndex] || 0)
    })
    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = this._formatMoney(grand)
  }

  _parseNumber(raw) {
    const cleaned = String(raw ?? "").replace(/[^\d.\-]/g, "")
    const value = parseFloat(cleaned)
    return Number.isFinite(value) ? value : 0
  }

  // Mirrors the server-rendered cells (symbol + 2dp + thousands) so
  // recomputed totals don't visibly change format mid-page.
  _formatMoney(value) {
    const symbol = this.element.dataset.cashFlowCurrencySymbol || "$"
    return symbol + value.toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    })
  }

  // ── AI import of a monthly spread (item 4) ─────────────────────────
  // Upload → AI preview → user maps/edits → Apply writes through the
  // same bulk endpoint the grid uses. The preview lives only in the DOM,
  // so abandoning the modal leaves nothing behind.

  showImportModal() {
    this._resetImportModal()
    this._show(this.importModalTarget)
  }

  hideImportModal() { this._hide(this.importModalTarget) }

  openImportFilePicker() { this.importFileInputTarget.click() }

  onImportDragOver(event) {
    event.preventDefault()
    this.importDropAreaTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  onImportDragLeave() {
    this.importDropAreaTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  onImportDrop(event) {
    event.preventDefault()
    this.onImportDragLeave()
    const file = event.dataTransfer?.files?.[0]
    if (file) this._uploadImportFile(file)
  }

  onImportFileChosen(event) {
    const file = event.currentTarget.files?.[0]
    if (file) this._uploadImportFile(file)
  }

  async _uploadImportFile(file) {
    this._hide(this.importDropzoneTarget)
    this._hide(this.importErrorTarget)
    this._show(this.importLoadingTarget)

    const form = new FormData()
    form.append("file", file)

    let resp
    try {
      const raw = await fetch(this.importUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
        body: form
      })
      resp = { ok: raw.ok, body: await raw.json().catch(() => null) }
    } catch (err) {
      resp = { ok: false, body: { error: err.message } }
    }

    this._hide(this.importLoadingTarget)
    if (!resp.ok) {
      this._showImportError(resp.body?.error || "We couldn't read that file.")
      return
    }

    this._importLines = resp.body.lines || []
    this._renderImportReview(resp.body.entries || [], resp.body.unmatched_count || 0)
  }

  _renderImportReview(entries, unmatchedCount) {
    if (entries.length === 0) {
      this._showImportError("We couldn't find any monthly amounts in that file.")
      return
    }

    const options = this._importLines
      .map(l => `<option value="${l.id}">${this._escape(l.label)}</option>`)
      .join("")

    this.importRowsTarget.innerHTML = entries.map((entry, i) => `
      <tr data-import-row="${i}"
          data-due-date="${this._escape(entry.due_date)}"
          data-amount="${entry.amount}">
        <td class="px-2 py-1.5 text-gray-700">${this._escape(entry.source || "—")}</td>
        <td class="px-2 py-1.5">
          <select data-import-line
                  class="w-full text-[13px] border border-gray-300 rounded px-1.5 py-1 bg-white cursor-pointer ${entry.budget_line_item_id ? "" : "border-amber-400 bg-amber-50"}">
            <option value="">— skip this row —</option>
            ${options}
          </select>
        </td>
        <td class="px-2 py-1.5 text-gray-600 tabular-nums">${this._escape(entry.month)}</td>
        <td class="px-2 py-1.5 text-right text-gray-800 tabular-nums">${this._formatMoney(entry.amount)}</td>
        <td class="px-2 py-1.5 text-center">
          <button type="button" data-action="click->cash-flow#removeImportRow"
                  class="text-gray-300 hover:text-red-600 cursor-pointer" aria-label="Remove row">✕</button>
        </td>
      </tr>
    `).join("")

    // Set the AI's guess after injection so the option list exists.
    entries.forEach((entry, i) => {
      if (!entry.budget_line_item_id) return
      const select = this.importRowsTarget.querySelector(`[data-import-row="${i}"] [data-import-line]`)
      if (select) select.value = String(entry.budget_line_item_id)
    })

    const matched = entries.length - unmatchedCount
    this.importSummaryTarget.innerHTML = unmatchedCount > 0
      ? `Found <strong>${entries.length}</strong> monthly amounts. <strong>${matched}</strong> matched a budget line; <strong>${unmatchedCount}</strong> need you to pick one.`
      : `Found <strong>${entries.length}</strong> monthly amounts, all matched to a budget line.`

    this._show(this.importReviewTarget)
    this._show(this.importApplyButtonTarget)
  }

  removeImportRow(event) {
    event.currentTarget.closest("tr")?.remove()
    if (this.importRowsTarget.children.length === 0) {
      this._hide(this.importApplyButtonTarget)
      this._showImportError("No rows left to import.")
    }
  }

  async applyImport() {
    const cells = Array.from(this.importRowsTarget.querySelectorAll("tr")).flatMap(row => {
      const lineId = row.querySelector("[data-import-line]")?.value
      if (!lineId) return []
      return [{
        budget_line_item_id: parseInt(lineId, 10),
        due_date: row.dataset.dueDate,
        amount: row.dataset.amount
      }]
    })

    if (cells.length === 0) {
      this._showImportError("Pick a budget line for at least one row first.")
      return
    }

    this.importApplyButtonTarget.disabled = true
    const resp = await this._ajaxJSON("POST", this.bulkUrlValue, { cells })
    this.importApplyButtonTarget.disabled = false

    if (!resp.ok) {
      this._showImportError(resp.body?.error || resp.body?.errors?.join(", ") || "Couldn't apply those rows.")
      return
    }

    this._track("Cash Flow Import Applied", { row_count: cells.length, saved_count: resp.body?.saved || 0 })
    this._hide(this.importModalTarget)
    this._flashAndReload(`Imported ${resp.body?.saved || cells.length} monthly amounts`)
  }

  _resetImportModal() {
    this._importLines = []
    this.importRowsTarget.innerHTML = ""
    this.importFileInputTarget.value = ""
    this.importSummaryTarget.innerHTML = ""
    this._show(this.importDropzoneTarget)
    this._hide(this.importLoadingTarget)
    this._hide(this.importReviewTarget)
    this._hide(this.importErrorTarget)
    this._hide(this.importApplyButtonTarget)
  }

  _showImportError(message) {
    this.importErrorTarget.textContent = message
    this._show(this.importErrorTarget)
    this._show(this.importDropzoneTarget)
  }

  // ── Forecast display mode (item 6): Cash Out ⇄ Balance Remaining ──
  // Server-side view switch — re-fetches the forecast panel with
  // `?mode=` so the extra columns render from the same calculator run.
  switchForecastMode(event) {
    const mode = event.currentTarget.dataset.modeParam
    if (!mode || mode === this._forecastMode()) return

    this._forecastModeValue = mode
    this._track("Cash Flow Forecast Mode Switched", { mode: mode })

    const url = new URL(window.location.href)
    url.searchParams.set("mode", mode)
    window.history.replaceState({}, "", url.toString())
    // Direct call — bypasses switchView's "already rendered" cache so the
    // panel re-renders in place with the new mode's columns.
    this._fetchAndRenderPanel("forecast")
  }

  _forecastMode() {
    if (this._forecastModeValue) return this._forecastModeValue
    return new URL(window.location.href).searchParams.get("mode") || "cash_out"
  }

  async _fetchAndRenderPanel(view) {
    const panel = this._panelFor(view)
    if (!panel) return

    panel.innerHTML = `<div class="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500 text-[13px]">Loading…</div>`
    this._setActiveView(view)

    const url = new URL(window.location.href)
    url.searchParams.set("view", view)
    url.searchParams.set("grain", this.grainValue)
    url.searchParams.set("horizon_months", this.horizonValue)
    url.searchParams.set("mode", this._forecastMode())

    try {
      const resp = await fetch(url.toString(), { headers: { "Accept": "text/html" } })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const html = await resp.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newPanel = doc.querySelector(`[data-cash-flow-target="viewPanel"][data-view="${view}"]`)
      if (newPanel) {
        panel.innerHTML = newPanel.innerHTML
      } else {
        panel.innerHTML = `<div class="bg-white border border-gray-200 rounded-lg p-6 text-center text-red-600 text-[13px]">Unable to load ${view}.</div>`
      }
      window.history.replaceState({}, "", url.toString())
    } catch (err) {
      panel.innerHTML = `<div class="bg-white border border-gray-200 rounded-lg p-6 text-center text-red-600 text-[13px]">Failed to load: ${this._escape(err.message)}</div>`
    }
  }

  _panelFor(view) {
    return this.viewPanelTargets.find(el => el.dataset.view === view)
  }

  _setActiveView(view) {
    this.activeViewValue = view
    this.viewTabButtonTargets.forEach(btn => {
      const active = btn.dataset.view === view
      btn.classList.toggle("border-brand", active)
      btn.classList.toggle("text-gray-900", active)
      btn.classList.toggle("font-medium", active)
      btn.classList.toggle("border-transparent", !active)
      btn.classList.toggle("text-gray-500", !active)
    })
    this.viewPanelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.view !== view)
    })
  }

  // ── As-of ──────────────────────────────────────────────────────────
  onAsOfSubmit() {
    const value = this.hasAsOfInputTarget ? this.asOfInputTarget.value : null
    this._track("Cash Flow As-Of Changed", { as_of: value, view: this.activeViewValue })
  }

  submitAsOf() {
    if (this.hasAsOfFormTarget) this.asOfFormTarget.requestSubmit()
  }

  // ── Configure dropdown ─────────────────────────────────────────────
  async updateGrain() {
    const value = this.grainTarget.value
    const resp = await this._patchSettings({ default_grain: value })
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to update granularity", "error")
      return
    }
    this._track("Cash Flow Grain Changed", { grain: value })
    this._flashAndReload("Granularity updated")
  }

  // Fired from two inputs — the Configure dropdown and the Period pill
  // (item 3) — so read whichever one the user actually typed in.
  async updateHorizon(event) {
    const source = event?.currentTarget || this.horizonTarget
    const value = parseInt(source.value, 10)
    if (!Number.isFinite(value) || value < 6 || value > 120) {
      this._showToast("Horizon must be between 6 and 120 months", "error")
      return
    }
    const resp = await this._patchSettings({ forecast_horizon_months: value })
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to update horizon", "error")
      return
    }
    this._track("Cash Flow Horizon Changed", { horizon_months: value })
    this._flashAndReload("Horizon updated")
  }

  async updateSchedule() {
    const value = this.scheduleTarget.value
    const payload = value ? { default_schedule_id: value } : { default_schedule_id: "" }
    const resp = await this._patchSettings(payload)
    if (resp.ok) {
      this._track("Cash Flow Settings Updated", { field: "default_schedule_id", value })
      this._flashAndReload("Default schedule updated")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to update schedule", "error")
    }
  }

  async _patchSettings(attrs) {
    return this._ajaxJSON("PATCH", this.settingsUrlValue, { cash_flow_setting: attrs })
  }

  // ── Forecast capture ───────────────────────────────────────────────
  showCaptureModal() {
    if (!this.hasCaptureModalTarget) return
    if (this.hasCaptureLabelTarget) this.captureLabelTarget.value = ""
    if (this.hasCaptureKindTarget) this.captureKindTarget.value = "adhoc"
    this._show(this.captureModalTarget)
  }

  hideCaptureModal() {
    if (this.hasCaptureModalTarget) this._hide(this.captureModalTarget)
  }

  async captureForecast() {
    const label = this.hasCaptureLabelTarget ? this.captureLabelTarget.value.trim() : ""
    const kind = this.hasCaptureKindTarget ? this.captureKindTarget.value : "adhoc"
    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/cash_flow_forecasts`
    const payload = { cash_flow_forecast: { label, kind } }

    const resp = await this._ajaxJSON("POST", url, payload)
    if (!resp.ok) {
      const msg = resp.body?.errors?.join(", ") || "Failed to capture forecast"
      this._showToast(msg, "error")
      return
    }

    this._track("Cash Flow Forecast Captured", {
      cash_flow_forecast_id: resp.body?.id,
      kind,
      label_provided: label.length > 0
    })
    this.hideCaptureModal()
    sessionStorage.setItem("cash_flow_flash", JSON.stringify({ message: "Forecast captured", type: "success" }))
    sessionStorage.setItem("cash_flow_open_forecasts_modal", "1")
    window.location.reload()
  }

  // ── Forecasts list modal ───────────────────────────────────────────
  showForecastsModal() {
    if (this.hasForecastsModalTarget) this._show(this.forecastsModalTarget)
  }

  hideForecastsModal() {
    if (this.hasForecastsModalTarget) this._hide(this.forecastsModalTarget)
  }

  // ── Overrides: modal open/close ────────────────────────────────────
  openNewOverrideModal() {
    this._editingOverrideId = null
    this.overrideModalTitleTarget.textContent = "New override"
    this.overrideLineTarget.disabled = false
    this.overrideLineTarget.value = this.overrideLineTarget.options[0]?.value || ""
    this.overrideDueDateTarget.value = new Date().toISOString().slice(0, 10)
    this.overrideAmountTarget.value = ""
    this.overrideReasonTarget.value = ""
    this._hideOverrideError()
    this._hide(this.overrideDeleteButtonTarget)
    this._show(this.overrideModalTarget)
  }

  openEditOverrideModal(event) {
    const row = event.currentTarget.closest("[data-cash-flow-target='overrideRow']")
    if (!row) return
    this._editingOverrideId = row.dataset.overrideId
    this.overrideModalTitleTarget.textContent = "Edit override"
    this.overrideLineTarget.value = row.dataset.lineId
    this.overrideLineTarget.disabled = true
    this.overrideDueDateTarget.value = row.dataset.dueDate
    this.overrideAmountTarget.value = row.dataset.amount
    this.overrideReasonTarget.value = row.dataset.reason || ""
    this._hideOverrideError()
    this._show(this.overrideDeleteButtonTarget)
    this._show(this.overrideModalTarget)
  }

  hideOverrideModal() {
    this._hide(this.overrideModalTarget)
  }

  // ── Overrides: save (create or update) ─────────────────────────────
  async saveOverride() {
    this._hideOverrideError()
    const lineId = this.overrideLineTarget.value
    const dueDate = this.overrideDueDateTarget.value
    const amount = parseFloat(this.overrideAmountTarget.value)
    const reason = this.overrideReasonTarget.value.trim()

    if (!lineId) return this._showOverrideError("Select a budget line item")
    if (!dueDate) return this._showOverrideError("Pick a due date")
    if (!Number.isFinite(amount) || amount <= 0) return this._showOverrideError("Amount must be greater than 0")

    const payload = { cash_flow_override: { due_date: dueDate, amount, reason } }

    let resp
    if (this._editingOverrideId) {
      resp = await this._ajaxJSON("PATCH", `/cash_flow_overrides/${this._editingOverrideId}`, payload)
    } else {
      resp = await this._ajaxJSON("POST", `/budget_line_items/${lineId}/cash_flow_overrides`, payload)
    }

    if (!resp.ok) {
      this._showOverrideError(resp.body?.errors?.join(", ") || "Save failed")
      return
    }

    this.hideOverrideModal()
    this._upsertOverrideRow(resp.body, lineId)

    if (this._editingOverrideId) {
      this._showToast("Override updated")
    } else {
      this._showToast("Override added")
    }
    this._editingOverrideId = null
  }

  // ── Overrides: delete ──────────────────────────────────────────────
  confirmDeleteOverride(event) {
    const row = event.currentTarget.closest("[data-cash-flow-target='overrideRow']")
    this._pendingDeleteId = row ? row.dataset.overrideId : this._editingOverrideId
    if (!this._pendingDeleteId) return
    this._show(this.deleteConfirmModalTarget)
  }

  hideDeleteConfirm() {
    this._hide(this.deleteConfirmModalTarget)
    this._pendingDeleteId = null
  }

  async deleteOverride() {
    const id = this._pendingDeleteId
    if (!id) return
    const resp = await this._ajaxJSON("DELETE", `/cash_flow_overrides/${id}`)
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Delete failed", "error")
      return
    }
    this._removeOverrideRow(id)
    this._hide(this.deleteConfirmModalTarget)
    this._hide(this.overrideModalTarget)
    this._pendingDeleteId = null
    this._editingOverrideId = null
    this._showToast("Override removed")
  }

  // ── DOM-patch helpers for the overrides table ──────────────────────
  _upsertOverrideRow(payload, lineId) {
    if (!payload || !payload.id) return
    const body = this.overrideTableBodyTarget
    this._removeEmptyRow()
    const existing = body.querySelector(`[data-override-id="${payload.id}"]`)
    if (existing) {
      this._populateRow(existing, payload)
      return
    }
    const lineOption = this.overrideLineTarget.querySelector(`option[value="${lineId}"]`)
    const lineName = lineOption ? lineOption.textContent.trim() : "Budget line"
    const row = document.createElement("tr")
    row.className = "hover:bg-gray-50"
    row.setAttribute("data-cash-flow-target", "overrideRow")
    row.dataset.overrideId = payload.id
    row.dataset.lineId = lineId
    row.dataset.lineName = lineName
    row.innerHTML = `
      <td class="px-2 py-1.5">
        <div class="text-[13px] text-gray-800">${this._escape(lineName)}</div>
      </td>
      <td class="px-2 py-1.5 text-gray-700 tabular-nums"></td>
      <td class="text-right px-2 py-1.5 tabular-nums text-emerald-700 font-medium"></td>
      <td class="px-2 py-1.5 text-gray-600 max-w-xs truncate"></td>
      <td class="px-2 py-1.5 text-right">
        <button type="button"
                data-action="click->cash-flow#openEditOverrideModal"
                class="inline-flex items-center p-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                title="Edit">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>
        </button>
        <button type="button"
                data-action="click->cash-flow#confirmDeleteOverride"
                class="inline-flex items-center p-1 text-gray-500 hover:text-red-600 cursor-pointer"
                title="Delete">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
        </button>
      </td>
    `
    body.appendChild(row)
    this._populateRow(row, payload)
  }

  _populateRow(row, payload) {
    row.dataset.dueDate = payload.due_date
    row.dataset.amount = payload.amount
    row.dataset.reason = payload.reason || ""
    const cells = row.querySelectorAll("td")
    if (cells.length >= 4) {
      cells[1].textContent = this._formatDateHuman(payload.due_date)
      cells[2].textContent = `$${this._formatAmount(payload.amount)}`
      cells[3].textContent = payload.reason || "—"
      cells[3].title = payload.reason || ""
    }
  }

  _removeOverrideRow(id) {
    const row = this.overrideTableBodyTarget.querySelector(`[data-override-id="${id}"]`)
    if (row) row.remove()
    if (!this.overrideTableBodyTarget.querySelector("[data-cash-flow-target='overrideRow']")) {
      this._insertEmptyRow()
    }
  }

  _removeEmptyRow() {
    if (this.hasOverrideEmptyRowTarget) this.overrideEmptyRowTarget.remove()
  }

  _insertEmptyRow() {
    if (this.hasOverrideEmptyRowTarget) return
    const tr = document.createElement("tr")
    tr.setAttribute("data-cash-flow-target", "overrideEmptyRow")
    tr.innerHTML = `<td colspan="5" class="px-4 py-8 text-center text-[13px] text-gray-500">No overrides yet. Add one to pin a specific cash-out to a date and line.</td>`
    this.overrideTableBodyTarget.appendChild(tr)
  }

  _formatDateHuman(iso) {
    const d = new Date(`${iso}T00:00:00`)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  }

  _formatAmount(n) {
    const v = Number(n)
    if (!Number.isFinite(v)) return String(n)
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  _showOverrideError(message) {
    this.overrideErrorTarget.textContent = message
    this.overrideErrorTarget.classList.remove("hidden")
  }

  _hideOverrideError() {
    this.overrideErrorTarget.classList.add("hidden")
    this.overrideErrorTarget.textContent = ""
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── Shared helpers (parallel to eva_controller.js) ─────────────────
  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  _show(el) { el?.classList.remove("hidden") }
  _hide(el) { el?.classList.add("hidden") }

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("cash_flow_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("cash_flow_flash")
    if (!raw) return
    sessionStorage.removeItem("cash_flow_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const iconSvg = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = iconSvg
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }

    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }
}
